import { MultiMaterial } from '@babylonjs/core/Materials/multiMaterial';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Scene } from '@babylonjs/core/scene';
import { SubmeshFeature } from '@kb3d/models/classes/features/submesh-feature';
import { Token } from '@kb3d/models/classes/token';
import { ITrackedClass } from '@kb3d/models/interfaces/tracked-class';
import { KbViewer } from '@kb3d/view/kb-viewer';
import { RendererManager } from '../renderer-manager';
import { FeatureRenderer, SubmeshVertexDataMap } from './feature';
import {
    SubmeshDependencyData,
    SubmeshGeometryProcessor,
    SubmeshWorkerPayload,
} from './geometry-processor/submesh-geometry';
import { SubmeshVertexData } from '@kb3d/view/helpers/geometry-helper';

const multiMaterialUseCount = new Map<MultiMaterial, number>();
const featureMappings = new Map<string, { multiMaterial?: MultiMaterial; multiMaterialIndex?: number }>();

export class SubmeshRenderer extends FeatureRenderer<SubmeshFeature> {
    modelToken = Token.SubmeshFeature;
    processor = new SubmeshGeometryProcessor(this.scene);

    constructor(viewer: KbViewer, scene: Scene, private rendererManager: RendererManager) {
        super(viewer, scene);
    }

    async makeWorkerPayload(feature: SubmeshFeature, geometryData: SubmeshVertexDataMap) {
        const payload = await super.makeWorkerPayload(feature, geometryData);
        const submeshFeatureMapping = featureMappings.get(feature._dynamicId);
        if (!submeshFeatureMapping) {
            console.warn(`INFO: Submesh mapping not found for ${feature.name}`);
            return payload;
        }

        const featureData: SubmeshWorkerPayload = {
            multiMaterialIndex: submeshFeatureMapping.multiMaterialIndex,
        };
        payload.featureData = featureData;
        return payload;
    }

    updateMeshGeometry(
        feature: SubmeshFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>
    ) {
        const meshDynamicId = feature._parent?._dynamicId || '';
        const submeshFeatureMapping = featureMappings.get(feature._dynamicId);
        if (!submeshFeatureMapping) {
            console.warn(`INFO: Submesh mapping not found for ${feature.name}`);
            // This will just return the same vertex data
            return this.processor.updateMeshGeometry(feature, vertexData, vertexDataChanges, {
                meshDynamicId,
            });
        }

        const dependencyData: SubmeshDependencyData = {
            multiMaterialIndex: submeshFeatureMapping.multiMaterialIndex,
        };
        return this.processor.updateMeshGeometry(feature, vertexData, vertexDataChanges, {
            meshDynamicId,
            data: dependencyData,
        });
    }

    update3dInternal(feature: ITrackedClass<SubmeshFeature>) {
        let _submeshFeatureMapping = featureMappings.get(feature._dynamicId);

        if (feature.changes.has('materialId') || !_submeshFeatureMapping) {
            const meshNode = feature._parent!;
            const mesh = this.viewer.getRendererNode(meshNode) as Mesh;

            if (!mesh) {
                return;
            }

            if (!_submeshFeatureMapping) {
                _submeshFeatureMapping = {};
                featureMappings.set(feature._dynamicId, _submeshFeatureMapping);
            }

            const submeshFeatureMapping = _submeshFeatureMapping;

            if (feature.materialId) {
                if (!(mesh.material instanceof MultiMaterial)) {
                    const multiMaterial = new MultiMaterial(meshNode._dynamicId, this.scene);
                    if (mesh.material) {
                        multiMaterial.subMaterials[0] = mesh.material;
                    } else if (!mesh.material) {
                        // If a submesh multimaterial has a null material at index 0, it causes shadows to not work
                        multiMaterial.subMaterials[0] = this.rendererManager.material.defaultMaterial;
                    }
                    submeshFeatureMapping.multiMaterial = multiMaterial;
                    multiMaterialUseCount.set(multiMaterial, 0);
                    mesh.material = multiMaterial;
                } else {
                    submeshFeatureMapping.multiMaterial = mesh.material;
                }

                this.rendererManager.mesh.fetchMaterialNode(meshNode, feature.materialId).then(
                    materialNode => {
                        // The material may have changed on the mesh, so we have to re-fetch the reference just in case.
                        if (!(mesh.material instanceof MultiMaterial)) {
                            throw Error('Another script has removed the multimaterial on a mesh');
                        }
                        const multiMaterial = mesh.material;
                        if (multiMaterial !== submeshFeatureMapping.multiMaterial) {
                            console.warn(
                                `Submesh feature ${feature.name} lost reference to a multimaterial. Possible memory leak?`
                            );
                            submeshFeatureMapping.multiMaterial = multiMaterial;
                        }

                        // If the material is already part of the submaterials, do nothing
                        const existingSubmaterialIndex = multiMaterial.subMaterials.findIndex(
                            material => material?.id === materialNode._dynamicId
                        );
                        if (submeshFeatureMapping?.multiMaterialIndex === existingSubmaterialIndex) {
                            return;
                        } else if (existingSubmaterialIndex > -1) {
                            submeshFeatureMapping.multiMaterialIndex = existingSubmaterialIndex;

                            // Triggers change on this feature so the feature stack refreshes with the material index on this submesh
                            feature.setPropertyIsModified('vertexGroups', true);
                        } else {
                            // init the material if the node is showing
                            if (meshNode.visible) {
                                materialNode.init();
                            }

                            const material = this.viewer.getRendererNode(materialNode);
                            if (!material) {
                                throw Error('Material could not be found in renderer. Has the material been created?');
                            }

                            submeshFeatureMapping.multiMaterialIndex = multiMaterial.subMaterials.length;
                            multiMaterial.subMaterials.push(material);
                            multiMaterialUseCount.set(
                                multiMaterial,
                                (multiMaterialUseCount.get(multiMaterial) || 0) + 1
                            );

                            // Triggers change on this feature so the feature stack refreshes with the material index on this submesh
                            feature.setPropertyIsModified('vertexGroups', true);
                        }
                    },
                    err => {
                        feature.materialId = '';
                    }
                );
            }
        }
    }

    public delete3d(feature: ITrackedClass<SubmeshFeature>, preserveCache?: boolean): void {
        if (!preserveCache) {
            const submeshFeatureMapping = featureMappings.get(feature._dynamicId);
            if (submeshFeatureMapping && submeshFeatureMapping.multiMaterial) {
                const multiMaterial = submeshFeatureMapping.multiMaterial;
                const count = multiMaterialUseCount.get(multiMaterial);

                if (count !== undefined) {
                    if (count <= 1) {
                        const meshNode = feature._parent!;
                        const mesh = this.viewer.getRendererNode(meshNode) as Mesh;
                        if (mesh) {
                            mesh.material = multiMaterial.getSubMaterial(0);
                        }
                        multiMaterialUseCount.delete(multiMaterial);
                        multiMaterial.dispose(true, true, false);
                    } else {
                        multiMaterialUseCount.set(multiMaterial, count - 1);
                    }
                }
            }
            featureMappings.delete(feature._dynamicId);
        }
        super.delete3d(feature, preserveCache);
    }
}
